import React, { useEffect, useState, useRef } from 'react'
import AGGrid from "../../../Components/AGGrid";
import { CommonUtil } from "../../../utils/CommonUtil";
import { NetworkManager } from "../../../Components/NetworkManager";
import { Constants } from "../../Languages/Constants";
import Offcanvas from "../../../Components/OffCanvas";
import GenericPopup from "../../../Components/GenericPopup";
import InputField from '../../../Components/InputField';
import TextAreaField from '../../../Components/TextAreaField';
import JSONCompareViewer from '../../../Components/JSONCompareViewer';
import ToastManager from '../../../Components/ToastManager';
import HistoryViewer from './HistoryViewer';
import InvoiceDetailViewer from './InvoiceDetailViewer';

const PendingInvoices = () => {
    const gridRef = useRef<any>(null);
    const popupGridRef = useRef<any>(null);
    const [rowsData, setRowsData] = useState<any>([]);
    const [showPopup, setShowPopup] = useState(false);
    const [selectedRowData, setSelectedRowData] = useState<any>(null);
    const [groupDetails, setGroupDetails] = useState<any>(null);
    const [activeTab, setActiveTab] = useState('invoice');
    const [showHistory, setShowHistory] = useState(false);
    const [historyData, setHistoryData] = useState<any>([]);
    const [selectedGroupCodes, setSelectedGroupCodes] = useState<string[]>([]);

    const fetchGroupDetails = (groupCode: string) => {
        NetworkManager.sendJsonRequest(`api/prextra/review/group/${groupCode}`, '', '', 'GET', true, (data: any) => {
            if (data) {
                const parsedData = JSON.parse(data);
                setGroupDetails(parsedData);
                setShowPopup(true);
            }
        }, (error: any) => {
            console.error(error);
        });
    }

    const handleViewHistory = (tab: string, id: any) => {
        setActiveTab(tab);
        NetworkManager.sendJsonRequest(`api/prextra/review/history/pending/${id}`, '', '', 'GET', true, (data: any) => {
            if (data) {
                setHistoryData(JSON.parse(data));
                setShowHistory(true);
            }
        }, (error: any) => {
            console.error(error);
        });
    }

    const invoiceButtonRender = (propData: any) => {
        const onClickInvoice = () => {
            setSelectedRowData(propData.data);
            fetchGroupDetails(propData.data.groupCode);
        }
        return (
            <button className="btn btn-primary btn-sm mb-1" style={{ cursor: 'pointer', minWidth: '200px' }} onClick={onClickInvoice}>{propData.data.groupCode}</button>
        )
    }

    const isRowSelectable = (rowNode: any) => {
        return rowNode.data ? !rowNode.data.isFlagged : true;
    }

    const pendingInvoicesCol: any = [
        {
            headerCheckboxSelection: true,
            headerCheckboxSelectionFilteredOnly: true,
            checkboxSelection: true,
            width: 70,
            maxWidth: 100,
            resizable: false,
            sortable: false,
            filter: false,
            suppressMenu: true,
            cellClass: 'ag-checkbox-center',
            headerClass: 'ag-checkbox-center'
        },
        { field: 'button', headerName: "Invoices", resizable: true, sortable: true, flex: 1, filter: 'agNumberColumnFilter', cellRenderer: invoiceButtonRender },
        { field: 'reservationCode', headerName: CommonUtil.getMessageText(Constants.RESERVATION_CODE, "Reservation Code"), resizable: true, sortable: true, flex: 1, filter: 'agNumberColumnFilter' },
        { field: 'unitNumber', headerName: CommonUtil.getMessageText(Constants.UNIT_NUMBER, "Unit Number"), resizable: true, sortable: true, flex: 1, filter: 'agTextColumnFilter' },
        { field: 'vinNumber', headerName: CommonUtil.getMessageText(Constants.VIN_NUMBER, "VIN Number"), resizable: true, sortable: true, flex: 1, filter: 'agTextColumnFilter' },
        { field: 'invoiceTotalAmount', headerName: "Invoice Total Amount", resizable: true, sortable: true, flex: 1, filter: 'agTextColumnFilter' },
        { field: 'statementTotalAmount', headerName: "Statement Total Amount", resizable: true, sortable: true, flex: 1, filter: 'agTextColumnFilter' },
        { field: 'ownerOrganization', headerName: CommonUtil.getMessageText(Constants.OWNER_ORGANIZATION, "Owner Organization"), resizable: true, sortable: true, flex: 1, filter: 'agNumberColumnFilter' },
        { field: 'renterOrganization', headerName: CommonUtil.getMessageText(Constants.RENTER_ORGANIZATION, "Renter Organization"), resizable: true, sortable: true, flex: 1, filter: 'agNumberColumnFilter' },
    ];

    useEffect(() => {
        fetchPendingBills();
    }, []);

    const fetchPendingBills = () => {
        NetworkManager.sendJsonRequest('api/prextra/review/pendingInvoices', '', '', 'GET', true, (data: any) => {
            if (data) {
                setRowsData(JSON.parse(data));
                setSelectedGroupCodes([]);
            }
        }, (error: any) => {
            console.error(error);
        });
    }

    const handleSelectionChanged = () => {
        const selectedRows = gridRef.current?.getSelectedRows();
        const groupCodes = selectedRows ? selectedRows.map((row: any) => row.groupCode) : [];
        setSelectedGroupCodes(groupCodes);
    }

    const handleSendToPrextraSelected = () => {
        if (selectedGroupCodes.length === 0) return;

        const payload = {
            groupCodes: selectedGroupCodes
        };

        NetworkManager.sendJsonRequest('api/prextra/review/sendToPrextra', payload, '', 'POST', true, (res: any) => {
            if (res) {
                const response = JSON.parse(res);
                if (!response.errorMessage) {
                    ToastManager.showToast("Sent to Prextra Successfully", 'Success');
                    fetchPendingBills();
                } else {
                    ToastManager.showToast(response.errorMessage, 'Error');
                }
            }
        }, (err: any) => {
            console.error(err);
        });
    }

    return (
        <div className="container-fluid mt-3">
            <div className="ag-theme-alpine" style={{ height: "750px", width: "100%" }}>
                <AGGrid
                    id="PendingInvoicesGrid"
                    cols={pendingInvoicesCol}
                    rows={rowsData}
                    pageSize={500}
                    headerHeight={CommonUtil.GRID_HEADER_HEIGHT}
                    rowHeight={CommonUtil.GRID_ROW_HEIGHT}
                    ref={gridRef}
                    addNew={false}
                    rowSelection="multiple"
                    suppressRowClickSelection={true}
                    isRowSelectable={isRowSelectable}
                    onSelectionChanged={handleSelectionChanged}
                    getRowStyle={(params: any) => {
                        if (params.data?.isFlagged) {
                            return {
                                backgroundColor: '#f8d7da',
                                color: '#721c24'
                            };
                        }
                        return {};
                    }}
                />
            </div>
            <div className="d-flex justify-content-end mt-3">
                <button 
                    className="btn btn-primary me-3" 
                    onClick={handleSendToPrextraSelected}
                    disabled={selectedGroupCodes.length === 0}
                >
                    Send To Prextra
                </button>
            </div>

            <Offcanvas
                show={showPopup}
                onHide={() => {setShowPopup(false); fetchPendingBills();}}
                title={`Invoice Details - ${selectedRowData?.groupCode || ''}`}
                size="90%"
                placement="end"
                scrollable={false}
            >
                {showPopup && (
                    <InvoiceDetailViewer
                        groupDetails={groupDetails}
                        groupCode={selectedRowData?.groupCode}
                        onRefresh={fetchGroupDetails}
                        onViewHistory={handleViewHistory}
                        onClose={() => {setShowPopup(false); fetchPendingBills();}}
                    />
                )}
            </Offcanvas>
            <GenericPopup
                header={`Review ${activeTab} history - ${selectedRowData?.groupCode || ''}`}
                mode={showHistory ? "view" : null}
                onClose={() => setShowHistory(false)}
                fullscreen={true}
            >
                <HistoryViewer historyData={historyData} onClose={() => setShowHistory(false)} />
            </GenericPopup>
        </div>
    )
}

export default PendingInvoices